import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, UserCircle } from "lucide-react";
import { useAuth } from "./AuthContext";
import LanguageSwitcher from "../components/LanguageSwitcher";

/**
 * Page /profile — préférences du compte utilisateur connecté.
 * Phase 1 : email + rôle + portée + sélecteur de langue. Phase
 * ultérieure (out of scope ADR i18n) : timezone, notifications.
 *
 * Cette page lit `email` et `locale` enrichis par AuthContext via
 * `/auth/me` après login. Si `email` est `undefined`, c'est que le
 * fetch /auth/me a échoué — on affiche le UUID en fallback.
 */
export default function Profile() {
  const { t } = useTranslation(["auth", "common"]);
  const { user } = useAuth();

  if (!user) return null;

  return (
    <div className="max-w-2xl mx-auto py-8">
      <header className="flex items-center gap-3 mb-6">
        <UserCircle size={28} style={{ color: "var(--color-accent)" }} />
        <div>
          <h1 className="text-2xl font-bold" style={{ color: "var(--color-fg)" }}>
            {t("auth:profile.title")}
          </h1>
          <p className="text-sm" style={{ color: "#94a3b8" }}>
            {t("auth:profile.subtitle")}
          </p>
        </div>
      </header>

      <dl
        className="divide-y rounded-lg border"
        style={{ borderColor: "var(--color-border)", background: "var(--color-surface)" }}
      >
        <Row label={t("auth:profile.email")}>
          <code className="text-sm">{user.email ?? user.sub}</code>
        </Row>
        <Row label={t("auth:profile.role")}>
          <code className="text-sm">{user.role_v2 ?? user.role ?? "?"}</code>
        </Row>
        <Row label={t("auth:profile.scope")}>
          <ScopeBadge scope={user.scope} t={t} />
        </Row>
        <Row label={t("auth:profile.language")} help={t("auth:profile.language_help")}>
          <LanguageSwitcher />
        </Row>
      </dl>

      <p className="mt-6">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm hover:underline"
          style={{ color: "var(--color-accent)" }}
        >
          <ArrowLeft size={14} />
          {t("auth:profile.back_to_app")}
        </Link>
      </p>
    </div>
  );
}

function Row({ label, help, children }) {
  return (
    <div
      className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-4 py-3"
      style={{ borderColor: "var(--color-border)" }}
    >
      <div className="min-w-0">
        <dt className="text-xs uppercase tracking-wider" style={{ color: "#94a3b8" }}>
          {label}
        </dt>
        {help && (
          <p className="text-xs mt-0.5" style={{ color: "#64748b" }}>
            {help}
          </p>
        )}
      </div>
      <dd>{children}</dd>
    </div>
  );
}

function ScopeBadge({ scope, t }) {
  if (!scope) return <span className="text-sm" style={{ color: "#64748b" }}>—</span>;
  if (scope.kind === "platform") {
    return <code className="text-sm">platform</code>;
  }
  if (scope.kind === "mssp") {
    return (
      <code className="text-sm">
        mssp · <span style={{ color: "#94a3b8" }}>{scope.mssp_id?.slice(0, 8) ?? "?"}</span>
      </code>
    );
  }
  return (
    <code className="text-sm">
      tenant · <span style={{ color: "#94a3b8" }}>{scope.tenant_id?.slice(0, 8) ?? "?"}</span>
    </code>
  );
}
